import React, { useEffect, useRef, useState, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Area, Button } from '../styles/_app.style';
import {
  RegisterArea,
  FormArea,
  Form,
  Tagline,
  FormActions,
} from '../styles/login.style';
import { BookLogo } from '../components/common/BookLogo';
import { NAVIGATION_HEIGHT } from '../constants/global';
import { useScale } from './HeightScaler';
import { baseURL, endpoints } from '../constants/endpoints';
import axios from 'axios';
import { useUser } from '../contextManager/user.context';
import { AxiosContext } from '../contextManager/axios.context';

type Status = 'loading' | 'success' | 'already' | 'error';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { scale } = useScale();
  const [status, setStatus] = useState<Status>('loading');
  const calledRef = useRef(false);
  const { userDetails, storeUserDetails } = useUser();
  const authedAxios = useContext(AxiosContext);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      return;
    }

    axios
      .get(`${baseURL}/verify/confirm`, { params: { token } })
      .then(async (res) => {
        if (res.data.message === 'Email already verified') {
          setStatus('already');
        } else {
          setStatus('success');
        }
        // If logged in, refresh userDetails so banners disappear
        if (userDetails) {
          try {
            const { data: updatedUser } = await authedAxios.get(endpoints.profile);
            const tok = localStorage.getItem('accessToken') || '';
            storeUserDetails(updatedUser, tok);
          } catch {}
        }
      })
      .catch(() => {
        setStatus('error');
      });
  }, [searchParams]);

  const messages: Record<Status, { title: string; body: string }> = {
    loading: {
      title: 'Verifying…',
      body: 'Hang tight while we verify your email address.',
    },
    success: {
      title: 'Email verified!',
      body: 'Your account is now fully active. You can start using tute.',
    },
    already: {
      title: 'Already verified',
      body: 'Your email has already been verified. You\'re all set!',
    },
    error: {
      title: 'Verification failed',
      body: 'This link is invalid or has expired. Try requesting a new verification email from your dashboard.',
    },
  };

  const { title, body } = messages[status];
  const isLoggedIn = !!userDetails;

  return (
    <>
      <Helmet>
        <title>Verify Email - tute</title>
      </Helmet>
      <RegisterArea>
        <Area
          css={{
            height: `calc((100dvh / ${scale}) - ${NAVIGATION_HEIGHT})`,
            marginTop: `calc(${NAVIGATION_HEIGHT})`,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <FormArea>
            <Form onSubmit={(e) => e.preventDefault()}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: 'max-content',
                  alignItems: 'center',
                  marginBottom: '0.5rem',
                }}
              >
                <span
                  style={{
                    fontWeight: 'bold',
                    fontSize: '1.8rem',
                    lineHeight: '1',
                    color: 'var(--colors-slate12, #1e293b)',
                    letterSpacing: '-0.5px',
                  }}
                >
                  tute
                </span>
                <div style={{ width: '100%', height: '16px', marginTop: '0.1rem' }}>
                  <BookLogo />
                </div>
              </div>

              <Tagline style={{ marginBottom: '0.5rem', fontWeight: 600, fontSize: '1.1rem' }}>
                {title}
              </Tagline>

              <p style={{ color: '#666', textAlign: 'center', margin: '1rem 0' }}>
                {body}
              </p>

              <FormActions>
                <Button
                  type="button"
                  block
                  tone="success"
                  onClick={() => navigate(isLoggedIn ? '/profile' : '/login')}
                >
                  {status === 'success' || status === 'already'
                    ? isLoggedIn ? 'Go to Profile' : 'Go to Login'
                    : 'Back to Login'}
                </Button>
              </FormActions>
            </Form>
          </FormArea>
        </Area>
      </RegisterArea>
    </>
  );
};

export default VerifyEmail;